import {
  addMonths,
  format,
  isBefore,
  isToday,
  isWeekend,
  parseISO,
  startOfDay,
} from "date-fns"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useMemo } from "react"
import { useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { formatMoney } from "@/helpers/format"
import {
  useReceivables,
  useScheduleSearchParams,
} from "@/hooks"
import { cn } from "@/lib/utils"
import { DateHelper } from "@/utils"
export const ScheduleCalendar: React.FC = () => {
  const navigate = useNavigate()
  const { params } = useScheduleSearchParams()
  const currentDate = params.date || params.startDate
  const { data, isFetching } = useReceivables(params)
  const parsedCurrentDate = useMemo(
    () => parseISO(currentDate),
    [currentDate]
  )
  const firstDayOfMonth = useMemo(
    () =>
      parseISO(
        DateHelper.startOfMonth(currentDate)
      ).getDay(),
    [currentDate]
  )
  const monthData = useMemo(() => {
    return data?.map((item) => ({
      ...item,
      status: item.semaphore,
      monthValue: item.netValue,
      day: new Date(item.date + "T12:00").getDate(),
    }))
  }, [data])
  const daysOfMonth = useMemo(() => {
    return DateHelper.eachDayOfInterval({
      start: params.startDate,
      end: params.endDate,
    })
  }, [params.startDate, params.endDate])
  const leadingEmptyDays = useMemo(
    () => Array.from({ length: firstDayOfMonth }),
    [firstDayOfMonth]
  )
  const trailingEmptyDays = useMemo(() => {
    const remainingDays =
      (firstDayOfMonth + daysOfMonth.length) % 7
    return Array.from({
      length: remainingDays === 0 ? 0 : 7 - remainingDays,
    })
  }, [firstDayOfMonth, daysOfMonth.length])
  const updateURLParams = (date: string) => {
    const searchParams = new URLSearchParams()
    searchParams.set("date", date)
    searchParams.set(
      "startDate",
      DateHelper.startOfMonth(date)
    )
    searchParams.set("endDate", DateHelper.endOfMonth(date))
    navigate(`?${searchParams.toString()}`)
  }
  const handleGoToMonth = (direction: "prev" | "next") => {
    const newDate = format(
      addMonths(
        parsedCurrentDate,
        direction === "prev" ? -1 : 1
      ),
      "yyyy-MM-dd"
    )
    updateURLParams(newDate)
  }
  const handleClickDay = (date: string) => {
    const searchParams = new URLSearchParams()
    searchParams.set("date", date)
    searchParams.set("startDate", params.startDate)
    searchParams.set("endDate", params.endDate)
    navigate(`?${searchParams.toString()}`)
  }
  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-center space-y-0 pb-4">
        <div className="flex w-full items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleGoToMonth("prev")}
            aria-label="Mês anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="w-40 flex-1 text-center text-lg font-medium capitalize">
            {DateHelper.formatDate(
              currentDate,
              "MMMM/yyyy"
            )}
          </h2>
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleGoToMonth("next")}
            aria-label="Próximo mês"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid px-2 pb-4 sm:px-6">
        <div className="mb-1 grid grid-cols-7 gap-0.5 text-xs sm:mb-2 sm:gap-1 sm:text-sm">
          {[
            { short: "D", full: "Dom" },
            { short: "S", full: "Seg" },
            { short: "T", full: "Ter" },
            { short: "Q", full: "Qua" },
            { short: "Q", full: "Qui" },
            { short: "S", full: "Sex" },
            { short: "S", full: "Sáb" },
          ].map((day, index) => (
            <div
              key={index}
              className="py-1 text-center font-medium sm:py-2"
            >
              <span className="sm:hidden">{day.short}</span>
              <span className="hidden sm:inline">
                {day.full}
              </span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
          {leadingEmptyDays.map((_, index) => (
            <div
              key={`leading-empty-${index}`}
              aria-hidden="true"
              className="aspect-square"
            />
          ))}
          {daysOfMonth.map((date) => {
            const day = date.getDate()
            const dayData = monthData?.find(
              (d) => d.day === day
            )
            const isWeekendDay = isWeekend(date)
            const isPastDay = isBefore(
              startOfDay(date),
              startOfDay(new Date())
            )
            const isTodayDay = isToday(date)

            let badgeColorClass = "bg-gray-400 text-white"
            let badgeStyle = {}
            if (isPastDay) {
              badgeColorClass = "bg-green-600 text-white"
            } else if (isTodayDay) {
              badgeColorClass = "text-white"
              badgeStyle = {
                backgroundColor:
                  "var(--whitelabel-background)",
              }
            }

            return (
              <button
                key={day}
                disabled={isWeekendDay}
                onClick={() =>
                  handleClickDay(format(date, "yyyy-MM-dd"))
                }
                className={cn(
                  "group relative flex aspect-square cursor-pointer items-start justify-start rounded-md border p-1 transition-colors sm:p-2",
                  isWeekendDay &&
                    "cursor-default bg-zinc-200 hover:bg-zinc-200"
                )}
              >
                <span className="text-xs sm:text-sm">
                  {day}
                </span>
                {isFetching ? (
                  <Skeleton className="absolute bottom-1 left-1 right-1 z-10 h-6" />
                ) : (
                  dayData?.monthValue !== 0 && (
                    <span
                      title={formatMoney(
                        dayData?.monthValue
                      )}
                      style={badgeStyle}
                      className={cn(
                        "absolute bottom-0.5 left-0.5 right-0.5 inline-block truncate rounded px-0.5 py-px text-[8px] font-medium leading-tight md:bottom-1 md:left-1 md:right-1 md:p-1 md:text-xs",
                        badgeColorClass
                      )}
                    >
                      {formatMoney(dayData?.monthValue)}
                    </span>
                  )
                )}
              </button>
            )
          })}
          {trailingEmptyDays.map((_, index) => (
            <div
              key={`trailing-empty-${index}`}
              aria-hidden="true"
              className="aspect-square"
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
